import { useState, useEffect, useCallback, useRef } from 'react';
import { getLatestQueryHistory } from '../IdeationService';
//import { fieldLabelMap } from '../../NewIdeationXPLR/NewIdeation/constants';
import { getFieldTypeFromCategory, getConversationTypeFromCategory } from '../utils';
import { fieldLabelMap } from '../../NewIdeationXPLR/shared/constants';
import { ChatSession } from '../types';

export const useDataXPLRSessions = (
  organization_id: string,
  searchQuery: string,
  selectedCategory: string,
  sortBy: string
) => {
  const [chatSessions, setChatSessions] = useState<any[]>([]);
  const [elasticChatSessions, setElasticChatSessions] = useState<any[]>([]);
  const [selectedElasticChat, setSelectedElasticChat] = useState<any>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const fixedLimit = 20;
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const prevParamsRef = useRef({ searchQuery, selectedCategory, organization_id, sortBy });

  const loadSessions = useCallback(
    async (
      fieldType?: string,
      sortFilter?: string,
      append: boolean = false,
      page?: number,
      category?: string
    ) => {
      try {
        if (append) {
          setIsLoadingMore(true);
          setHasReachedEnd(false);
        }

        if (!append) setIsInitialLoading(true);

        const sort = sortFilter || sortBy;
        const conversationType = getConversationTypeFromCategory(category ?? selectedCategory);
        const serviceFieldType = fieldType === 'All' ? undefined : fieldType;
        const targetPage = page || (append ? currentPage : 1);

        const sessions = await getLatestQueryHistory(
          fixedLimit,
          organization_id,
          serviceFieldType,
          false,
          '',
          sort,
          conversationType,
          targetPage
        );

        if (sessions?.data) {
          let sessionList = (sessions?.data || []).map((item: any) => ({
            id: item.id,
            use_case_title: item.session_name || 'Untitled Session',
            session_name: item.session_name,
            created_by: item.created_by_user_name || 'admin',
            updated_by: item.updated_by_user_name || '',
            created_at: item.created_at,
            updated_at: item.updated_at,
            usedInProcess: item.field_type
              ? fieldLabelMap
                ? fieldLabelMap[item.field_type] || item.field_type
                : item.field_type
              : 'Unknown',
            conversation_id: item.conversation_id,
            ideation_intake: item.ideation_intake || [],
            field_type: item.field_type,
            conversation_type: item.conversation_type,
            highlight: item.highlight,
            sequential_id: item.sequential_id,
          }));

          // Apply client-side search filtering
          if (searchQuery.trim() && !append) {
            const query = searchQuery.toLowerCase();
            sessionList = sessionList.filter((session) => {
              const titleMatches = session.use_case_title?.toLowerCase().includes(query);
              const nameMatches = session.session_name
                ? session.session_name.toLowerCase().includes(query)
                : false;
              const highlightMatches = session.highlight
                ? String(session.highlight).toLowerCase().includes(query)
                : false;

              return titleMatches || nameMatches || highlightMatches;
            });
          }

          setChatSessions((prev) => {
            const selected = selectedElasticChat;
            const filteredSessionList = selected
              ? sessionList.filter((s: any) => s.id !== selected.id)
              : sessionList;

            const existingIds = new Set(append ? prev.map((s) => s.id) : []);
            const newSessions = append
              ? filteredSessionList.filter((s: any) => !existingIds.has(s.id))
              : filteredSessionList;

            let updatedSessions = append ? [...prev, ...newSessions] : newSessions;

            if (selected) {
              updatedSessions = [
                selected,
                ...updatedSessions.filter((s: any) => s.id !== selected.id),
              ];
            }

            if (append && (!sessions?.data || sessions.data.length < fixedLimit)) {
              setHasReachedEnd(true);
            }

            return updatedSessions;
          });

          setElasticChatSessions((prev) => {
            const existingIds = new Set(append ? prev.map((s) => s.id) : []);
            const newSessions = sessionList.filter((s) => !existingIds.has(s.id));
            const updatedSessions = append ? [...prev, ...newSessions] : sessionList;

            if (append && (!sessions?.data || sessions.data.length < fixedLimit)) {
              setHasReachedEnd(true);
            }

            return updatedSessions;
          });
        }
      } catch (error) {
        console.error('Error loading chat sessions:', error);
      } finally {
        if (append) setIsLoadingMore(false);
        else setIsInitialLoading(false);
      }
    },
    [organization_id, sortBy, currentPage, searchQuery, selectedElasticChat, selectedCategory]
  );

  const loadElasticChatSessions = useCallback(
    async (
      fieldType?: string,
      sortFilter?: string,
      append: boolean = false,
      page?: number,
      category?: string
    ) => {
      try {
        if (append) {
          setIsLoadingMore(true);
          setHasReachedEnd(false);
        }

        const sort = sortFilter || sortBy;
        const conversationType = getConversationTypeFromCategory(category ?? selectedCategory);
        const serviceFieldType = fieldType === 'All' ? undefined : fieldType;
        const targetPage = page || (append ? currentPage : 1);

        const sessions = await getLatestQueryHistory(
          fixedLimit,
          organization_id,
          serviceFieldType,
          false,
          searchQuery,
          sort,
          conversationType,
          targetPage
        );

        if (sessions?.data) {
          let sessionList = (sessions?.data || []).map((item: any) => ({
            id: item.id,
            use_case_title: item.session_name || 'Untitled Session',
            session_name: item.session_name,
            created_by: item.created_by_user_name || 'admin',
            updated_by: item.updated_by_user_name || '',
            created_at: item.created_at,
            updated_at: item.updated_at,
            usedInProcess: item.field_type
              ? fieldLabelMap
                ? fieldLabelMap[item.field_type] || item.field_type
                : item.field_type
              : 'Unknown',
            conversation_id: item.conversation_id,
            ideation_intake: item.ideation_intake || [],
            field_type: item.field_type,
            conversation_type: item.conversation_type,
            highlight: item.highlight,
            sequential_id: item.sequential_id,
          }));

          setElasticChatSessions((prev) => {
            const existingIds = new Set(append ? prev.map((s) => s.id) : []);
            const newSessions = sessionList.filter((s) => !existingIds.has(s.id));
            const updatedSessions = append ? [...prev, ...newSessions] : sessionList;

            if (append && (!sessions?.data || sessions.data.length < fixedLimit)) {
              setHasReachedEnd(true);
            }

            return updatedSessions;
          });
        }
      } catch (error) {
        console.error('Error loading chat sessions:', error);
      } finally {
        if (append) setIsLoadingMore(false);
      }
    },
    [organization_id, sortBy, currentPage, searchQuery, selectedCategory]
  );

  const handleLoadMore = () => {
    if (!isLoadingMore && !hasReachedEnd) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadSessions(
        getFieldTypeFromCategory(selectedCategory),
        sortBy,
        true,
        nextPage,
        selectedCategory
      );
    }
  };

  // Single effect for category and search changes to avoid duplicate API calls
  useEffect(() => {
    if (selectedCategory && organization_id) {
      const fieldType = getFieldTypeFromCategory(selectedCategory);

      // Skip fetch if parameters haven't changed
      if (
        prevParamsRef.current.searchQuery === searchQuery &&
        prevParamsRef.current.selectedCategory === selectedCategory &&
        prevParamsRef.current.organization_id === organization_id &&
        prevParamsRef.current.sortBy === sortBy &&
        !isInitialLoading // Only skip if we've already loaded once
      ) {
        return;
      }

      prevParamsRef.current = { searchQuery, selectedCategory, organization_id, sortBy };

      setCurrentPage(1);
      setHasReachedEnd(false);

      if (searchQuery) {
        loadElasticChatSessions(fieldType, sortBy, false, 1);
      } else {
        setSelectedElasticChat(''); // Clear pinned chat when search is cleared
        loadSessions(fieldType, sortBy, false, 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchQuery, organization_id, sortBy]);

  /**
   * Optimistically prepend or update a session in the list without re-fetching.
   * Used immediately after a successful postQuery call so the new session
   * appears in the sidebar right away, before the server-side list is refreshed.
   */
  const addOrUpdateSession = useCallback((session: ChatSession) => {
    setChatSessions((prev) => {
      const existingIndex = prev.findIndex(
        (s) => s.id === session.id || s.conversation_id === session.conversation_id
      );
      if (existingIndex !== -1) {
        // Update in place
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...session };
        return updated;
      }
      // Prepend as a new entry
      return [session, ...prev];
    });
  }, []);

  return {
    chatSessions,
    setChatSessions,
    elasticChatSessions,
    setElasticChatSessions,
    selectedElasticChat,
    setSelectedElasticChat,
    isInitialLoading,
    currentPage,
    setCurrentPage,
    isLoadingMore,
    hasReachedEnd,
    loadSessions,
    loadElasticChatSessions,
    handleLoadMore,
    addOrUpdateSession,
  };
};
